import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { ChipList } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadworksStateContext } from "../../contexts/RoadworksContext"
import { useRoadworkUpdate } from "../../hooks/mutations/roadwork/useRoadworkUpdate"

import * as Styled from "./RoadworkTagsList.styled"

export function RoadworkTagsList(props) {

    const {
        className,
        roadwork,
        isEditable,
        forceDisplayAddButton,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setAlert } = useAlertsDispatchContext()

    const { project } = useRoadworksStateContext()
    const updateMutation = useRoadworkUpdate(roadwork, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkTagsList.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkTagsList.snackbar.success"),
    })
    const [displayedTags, setDisplayedTags] = useState(roadwork?.tags?.map(v => ({ label: v, value: v })))

    useEffect(() => {
        setDisplayedTags((roadwork?.tags || []).map(v => ({ label: v, value: v })))
    }, [roadwork])

    const sortTags = useCallback((tagsArray) => {
        return tagsArray?.sort((tag1, tag2) => {
            return tag1.localeCompare(tag2, undefined, { sensitivity: "base" })
        })
    }, [])

    const updateTags = useCallback((tags) => {
        updateMutation.mutate({ tags: tags })
    }, [updateMutation])

    const onAdd = (newTag) => {
        const tagList = displayedTags.map(v => v.value)
        const newChipList = tagList.concat(newTag)
        updateTags(newChipList)
    }

    const deleteTag = useCallback((tag) => {
        const chipList = displayedTags.map(v => v.value)
        const tagIndex = chipList.findIndex(t => t === tag.value)
        chipList.splice(tagIndex, 1)
        updateTags(chipList)
    }, [displayedTags, updateTags])

    const onDelete = (deletedTag) => {
        const tagIndex = displayedTags.find(tag => tag.value === deletedTag.value)
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.roadworkTagsList.alert.title", { tag: deletedTag.value }),
            alertText: t(I18N_NAMESPACE, "containers.roadworkTagsList.alert.text", { tag: deletedTag.value }),
            callback: () => deleteTag(tagIndex),
            severity: "warning",
        })
    }

    const addableTags = useMemo(() => {
        const filteredNetworkTags = (project?.tags || []).filter(tag => !displayedTags?.includes(tag))
        return sortTags(filteredNetworkTags)
    }, [displayedTags, project, sortTags])

    return <Styled.RoadworkTagsListContainer className={className}>
        <Styled.Title variant="Desktop & Mobile/Heading/H3">{t(I18N_NAMESPACE, "containers.roadworkTagsList.title")}</Styled.Title>
        <ChipList
            onAdd={isEditable && onAdd}
            onDelete={isEditable && onDelete}
            options={addableTags}
            values={displayedTags}
            variant="outlined"
            small
            forceDisplayAddButton={forceDisplayAddButton}
        />
    </Styled.RoadworkTagsListContainer>
}

RoadworkTagsList.propTypes = {
    className: PropTypes.string,
    roadwork: PropTypes.roadworkWithTasks.isRequired,
    isEditable: PropTypes.bool,
    forceDisplayAddButton: PropTypes.bool,

}

RoadworkTagsList.defaultProps = {
    isEditable: false,
    forceDisplayAddButton: false,
}